import { useState } from 'react';
import { IdleTimerProvider, IIdleTimer, useIdleTimer } from 'react-idle-timer';
import { GMDialog } from '../Dialog';
import { useFullReset, useKioskState } from '../../context/Kiosk/kiosk';
import { GMButton } from '../Button';
import { useLocalization } from '../../context/Localization/localization';
import { BaseIdleSessionTimeout } from '../../helpers/timeout';
import { useAdminLogout } from '../../context/Admin/admin';
import { IdleTimeoutProps } from './IdleTimeout.types';

// TODO: Rework this to finish testing


/**
 * A component that manages idle timeout and refreshing of the application.
 *
 * @param hideDialog - Whether the dialog should be hidden.
 * @param evokeOnStillActive - Function to be invoked when still active.
 * @param evokeOnTimeout - Function to be invoked on timeout.
 * @param children - Content to be displayed within the `IdleTimeout` component.
 */
export const IdleTimeout = ({
    hideDialog,
    evokeOnStillActive,
    evokeOnTimeout,
    children,
}: IdleTimeoutProps) => {
    const kiosk = useKioskState();
    const localization = useLocalization();
    const reset = useFullReset();
    const logout = useAdminLogout();

    const [idle, setIdle] = useState<boolean>(false);

    const idleTimeout: number = (kiosk.timeout ?? BaseIdleSessionTimeout) * 1000;

    const onIdle: VoidFunction = () => {
        if (evokeOnTimeout) {
            setIdle(evokeOnTimeout());
            idleTimer.start();
        }
        else if (!idle) {
            setIdle(true);
        }
    };

    const idleTimer: IIdleTimer = useIdleTimer({
        timeout: idleTimeout,
        debounce: 250,
        onIdle: hideDialog ? reset : onIdle,
    });

    const stillActive = () => {
        setIdle(false);
        evokeOnStillActive && evokeOnStillActive();
    };

    const onDlgTimeout = async () => {
        await logout(true);
        reset();
    };

    return (
        <>
            {children}
            <GMDialog
                ariaLabel={localization.SessionTimeout}
                open={idle}
                title={localization.SessionTimeout}
                actions={[
                    <GMButton
                        onClick={stillActive}
                        id={localization.Continue}
                        title={localization.Continue}
                        ariaLabel={localization.Continue}
                    >
                        {localization.Continue}
                    </GMButton>,
                ]}
            >
                <IdleTimerProvider
                    timeout={idleTimeout}
                    onIdle={onDlgTimeout}
                    debounce={250}
                />
                <p>{localization.AreYouStillThere}</p>
                <p>{localization.ClickOnContinueToRemainLoggedIn}</p>
            </GMDialog>
        </>
    );
};